import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { handleGet } from "../../controllers/controller.all";
import { sendNotificationToAdmin } from "../../controllers/controller.notification";
import { Send, Bell, Smartphone, School } from "lucide-react";

export default function SendGlobalNotification() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";

    const [loading, setLoading] = useState(false);
    const [ecoles, setEcoles] = useState([]);
    
    const [formData, setFormData] = useState({
        ecoleId: "",
        title: "",
        message: "",
        usePush: true,
        useSms: false
    });

    useEffect(() => {
        const fetchEcoles = async () => {
            await handleGet("/ecole", (res) => {
                if (res?.ecoles) setEcoles(res.ecoles);
            }, token);
        };
        if (token) fetchEcoles();
        
    }, [token]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const success = await sendNotificationToAdmin(
            formData.ecoleId,
            formData.title,
            formData.message,
            formData.usePush,
            formData.useSms,
            token
        );
        if (success) {
            setFormData(prev => ({ ...prev, title: "", message: "" }));
        }
        setLoading(false);
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto">
            <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Notifications Globales (Super Admin)</h1>
                <p className="text-sm text-gray-500 mt-1">Envoyez des messages importants aux administrateurs des écoles.</p>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-6 space-y-6">
                    {/* Ecole Selection */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                            <School className="w-4 h-4 text-gray-400" />
                            Sélectionner une école *
                        </label>
                        <select 
                            name="ecoleId" 
                            value={formData.ecoleId} 
                            onChange={handleChange} 
                            required 
                            className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                            <option value="">-- Choisir une école --</option>
                            <option value="ALL" className="font-bold">Toutes les écoles</option>
                            {ecoles.map(e => <option key={e._id} value={e._id}>{e.nom}</option>)}
                        </select>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Content */}
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Titre de la notification *</label>
                            <input 
                                type="text" 
                                name="title" 
                                value={formData.title} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ex: Mise à jour système" 
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Message *</label>
                            <textarea 
                                name="message" 
                                value={formData.message} 
                                onChange={handleChange} 
                                required 
                                rows="4" 
                                placeholder="Saisissez votre message ici..." 
                                className="w-full border border-gray-300 rounded-lg py-2.5 px-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            ></textarea>
                        </div>
                    </div>

                    <hr className="border-gray-100" />

                    {/* Options */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-medium text-gray-900">Canaux de communication</h3>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <label className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition ${formData.usePush ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <div className="flex h-5 items-center">
                                    <input type="checkbox" name="usePush" checked={formData.usePush} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><Bell className="w-4 h-4" /> Notification Push</span>
                                    <span className="text-xs text-gray-500">Apparait sur l'application mobile des admins.</span>
                                </div>
                            </label>

                            <label className={`flex items-start gap-3 p-4 border rounded-xl cursor-pointer transition ${formData.useSms ? 'border-indigo-500 bg-indigo-50/50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                <div className="flex h-5 items-center">
                                    <input type="checkbox" name="useSms" checked={formData.useSms} onChange={handleChange} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm font-medium text-gray-900 flex items-center gap-2"><Smartphone className="w-4 h-4" /> SMS</span>
                                    <span className="text-xs text-gray-500">Envoie un SMS aux numéros des admins.</span>
                                </div>
                            </label>
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex justify-end">
                    <button 
                        type="submit" 
                        disabled={loading || (!formData.usePush && !formData.useSms)} 
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                        <Send className="h-4 w-4" />
                        {loading ? "Envoi en cours..." : "Envoyer la notification globale"}
                    </button>
                </div>
            </form>
        </div>
    );
}
